<script setup>
import { computed, onBeforeUnmount, ref, watch } from 'vue';
import { COUNTDOWN_MAX_SECONDS, COUNTDOWN_WARNING_SECONDS, countdownCursorAngle, countdownProgress, isCriticalCountdown, normalizeCountdown } from '../domain/countdown.js';

const props = defineProps({ seconds: { type: Number, default: 0 }, t: { type: Function, required: true } });
const fraction = ref(Math.min(COUNTDOWN_MAX_SECONDS, normalizeCountdown(props.seconds)));
let syncValue = fraction.value;
let syncAt = performance.now();
let timer = 0;

const displaySeconds = computed(() => Math.max(0, Math.floor(normalizeCountdown(props.seconds))));
const progress = computed(() => countdownProgress(fraction.value));
const progressOffset = computed(() => 100 - (progress.value * 100));
// As remaining time decreases, the visible endpoint advances clockwise.
const cursorAngle = computed(() => countdownCursorAngle(fraction.value));
const critical = computed(() => isCriticalCountdown(displaySeconds.value));
const warningPercent = (COUNTDOWN_WARNING_SECONDS / COUNTDOWN_MAX_SECONDS) * 100;
const visibleWarningPercent = computed(() => Math.min(warningPercent, progress.value * 100));

function tick(now = performance.now()) {
  const elapsed = Math.max(0, (now - syncAt) / 1000);
  fraction.value = Math.max(0, Math.min(COUNTDOWN_MAX_SECONDS, syncValue - elapsed));
}

watch(() => props.seconds, (next, previous) => {
  const safe = Math.min(COUNTDOWN_MAX_SECONDS, normalizeCountdown(next));
  syncValue = safe;
  syncAt = performance.now();
  if (Number(next) > Number(previous) || safe === 0) fraction.value = safe;
}, { immediate: true });

timer = window.setInterval(tick, 100);
onBeforeUnmount(() => window.clearInterval(timer));
</script>

<template>
  <div class="game-video-timer" :class="{ critical, stopped: displaySeconds <= 0 }" :aria-label="`${t('message.countdownLabel', 'Countdown')} ${displaySeconds}`">
    <span class="game-timer-bezel" aria-hidden="true" />
    <svg class="game-timer-ring" viewBox="0 0 100 100" aria-hidden="true">
      <defs>
        <linearGradient id="wanliTimerGold" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ffffff"/><stop offset=".42" stop-color="#a9f1df"/><stop offset="1" stop-color="#4ca68f"/></linearGradient>
        <linearGradient id="wanliTimerRed" x1="0" y1="0" x2="1" y2="1"><stop offset="0" stop-color="#ff9a83"/><stop offset=".45" stop-color="#df493d"/><stop offset="1" stop-color="#8e1e22"/></linearGradient>
      </defs>
      <circle class="game-timer-track" cx="50" cy="50" r="44" pathLength="100" />
      <circle class="game-timer-progress" cx="50" cy="50" r="44" pathLength="100" :stroke-dashoffset="progressOffset" />
      <circle class="game-timer-warning-zone" cx="50" cy="50" r="44" pathLength="100" :stroke-dasharray="`${visibleWarningPercent} ${100-visibleWarningPercent}`" />
      <circle class="game-timer-inner-ring" cx="50" cy="50" r="35.5" />
    </svg>
    <span class="game-timer-scale" aria-hidden="true" />
    <span class="game-timer-crystal" aria-hidden="true" />
    <span v-if="displaySeconds > 10" class="game-timer-cursor" :style="{ transform: `rotate(${cursorAngle}deg)` }"><i /></span>
    <span class="game-timer-face"><span class="game-timer-value" :class="{ compact: displaySeconds > 99 }">{{ displaySeconds }}</span><small>SEC</small></span>
  </div>
</template>
